import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  CircularProgress, 
  Grid,
  Button,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from '../components/Layout';
import UserChatsList from '../components/UserChatsList';
import ChatMessages from '../components/ChatMessages';
import { getUserWithPhotoDetails } from '../services/users';

const UserChatsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Check if there's a chat ID in session storage (from previously viewing it)
  const [selectedChatId, setSelectedChatId] = useState(() => {
    const savedChatId = sessionStorage.getItem('returnToChatId');
    const savedOwnerUserId = sessionStorage.getItem('returnToUserChats');
    
    // Only use the saved chat ID if we're returning to the correct user's chats
    if (savedChatId && savedOwnerUserId === userId) {
      // Clear it once we've used it
      sessionStorage.removeItem('returnToChatId');
      sessionStorage.removeItem('returnToUserChats');
      return savedChatId;
    }
    
    // If we're viewing a different user's chats, don't restore the previous chat
    if (savedOwnerUserId !== userId) {
      // Don't clear the storage, as we may still want to return to that chat later
      return null;
    }
    
    return null;
  });

  // Fetch user details when the component loads
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const userData = await getUserWithPhotoDetails(userId);
        if (userData) {
          setUser(userData);
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserDetails();
  }, [userId]);

  // Handle chat selection
  const handleChatSelect = (chatId) => {
    setSelectedChatId(chatId);
  };

  // Handle back button from chat view
  const handleBackFromChat = () => {
    // Just clear the chat selection instead of navigating back
    setSelectedChatId(null);
  };

  // Handle back button to mimic browser back button
  const handleBack = () => {
    navigate(-1); // Navigate back in history
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ mt: 3, color: 'error.main' }}>
            <Typography variant="h5">Error</Typography>
            <Typography variant="body1">{error}</Typography>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg">
        {/* Header and Navigation */}
        <Box sx={{ mb: 3, mt: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={handleBack}
            >
              User Details
            </Link>
            <Typography color="text.primary">Chats</Typography>
          </Breadcrumbs>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              <Typography variant="h4" component="h1">
                {user ? `${user.profile?.name || 'User'}'s Chats` : 'User Chats'}
              </Typography>
            </Box>
            {user && (
              <Typography variant="subtitle1" color="text.secondary">
                @{user.profile?.userName || user.id}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Content */}
        {!selectedChatId ? (
          // Show chat list when no chat is selected
          <UserChatsList userId={userId} onChatSelect={handleChatSelect} />
        ) : (
          // Show chat messages when a chat is selected
          <ChatMessages chatId={selectedChatId} onBack={handleBackFromChat} />
        )}
      </Container>
    </Layout>
  );
};

export default UserChatsPage;